import {
  ApacCandidateHeader,
  ApacCandidateHeaderFooterLinkProvider,
} from '@seek/apac-candidate-header-footer';
import type { UserProfile } from '@seek/apac-candidate-header-footer/types';
import { getLanguageSwitcher } from '@seek/ca-myprofile-utils';
import { useMelwaysInfo } from '@seek/melways-react';
import { siteConfig } from '@seek/melways-sites';
import type { ComponentProps } from 'react';
import { useDispatch } from 'react-redux';

import { useAuthenticationStatus } from 'src/context/use-user';
import { headerFooterLinkClicked } from 'src/reducers/location';

import { useGetProfileAvatar } from '../Profile/Avatar/data';
import { usePersonalDetails } from '../Profile/PersonalDetails/data';

import { mapLinkAction } from './utils';

type ApacHeaderAuthenticationStatus = ComponentProps<
  typeof ApacCandidateHeader
>['authenticationStatus'];

export const HeaderContainer = () => {
  const dispatch = useDispatch();

  const { site } = useMelwaysInfo();
  const { personalDetails } = usePersonalDetails();
  const authenticationStatus = useAuthenticationStatus();
  const firstName = personalDetails?.personalDetails.firstName || '';
  const lastName = personalDetails?.personalDetails.lastName || '';
  const emailAddress = personalDetails?.emailAddress || '';
  const { profileAvatar } = useGetProfileAvatar();
  const queryParams =
    typeof window !== 'undefined' ? window.location.search : '';
  const currentSiteConfigLanguages = siteConfig[site].languages;

  const languageSwitcher = getLanguageSwitcher(
    currentSiteConfigLanguages,
    'profile/me',
    queryParams,
  );

  const userProfile: UserProfile = {
    firstName,
    lastName,
    email: emailAddress,
    profilePhotoUrl: profileAvatar?.image128Url ?? undefined,
  };

  return (
    <ApacCandidateHeaderFooterLinkProvider
      value={{
        sendLinkEvent: ({ linkContext }) => {
          dispatch(
            headerFooterLinkClicked({
              linkPosition: 'header',
              linkText: (linkContext?.source as string) || 'none',
              linkAction: linkContext?.openingOrClosing
                ? mapLinkAction(linkContext.openingOrClosing as string)
                : undefined,
            }),
          );
        },
      }}
    >
      <ApacCandidateHeader
        activePrimaryTab="profile"
        activeAuthedUserTab="authedUserTabProfile"
        authenticationStatus={
          authenticationStatus as ApacHeaderAuthenticationStatus
        }
        userProfile={userProfile}
        languageSwitcher={languageSwitcher}
        widthMode="large"
      />
    </ApacCandidateHeaderFooterLinkProvider>
  );
};
